import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import {
  MoreDetailsModalSections,
  MoreDetailsOptions
} from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import CustomConfigs from '~/constants/customConfigs';
import {
  ContactType,
  SUBSCRIPTION_PREFERENCE
} from '~/constants/vdp/signUpForAlerts';
import type { DeepPartial } from '~/types';
import { loadItemFromLocalStorage } from '~/utilities/localStorage';
import removeQueryParam from '~/utilities/removeQueryParam';
import { ModalId, ModalPriorities, WithModalOpenType } from './constants';

const dummyFn = () => {};

type WithContinue = { onContinue?: () => void };
type WithConfirmationContent = { onlyConfirmCancellation?: boolean };
type SignUpForAlertsModalOptions = { contactType: ContactType };

export type WhyBuyModalOptions = { openTo?: string };

type ContextType = {
  confirmModalContinue: () => void;
  isAboutReviewsModalOpen: boolean;
  isAccidentsModalOpen: boolean;
  isAccountModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isAvailabilityModalOpen: boolean;
  isCatalogOemModalOpen: boolean;
  isComposerUiModalOpen: boolean;
  isConfirmModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isFeaturesAndSpecsModalOpen: boolean;
  isFinanceActivationModalOpen: boolean;
  isFullScreenGalleryOpen: boolean;
  isGeicoDiscountModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isInspectionReportModalOpen: boolean;
  isLockTimerModalOpen: boolean;
  isMarketValueModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isOnLoadAlertsModalOpen: boolean;
  isOriginalBrochureModalOpen: boolean;
  isPartnerModalOpen: boolean;
  isPetitionModalOpen: boolean;
  isPreOrderModalOpen: boolean;
  isPurchaseCompletedModalOpen: boolean;
  isRecallModalOpen: boolean;
  isReservableCancelModalOpen: boolean;
  isReservableModalOpen: boolean;
  isReturnPolicyModalOpen: boolean;
  isRootDiscountModalOpen: boolean;
  isSampleTermsModalOpen: boolean;
  isSavingsModalOpen: boolean;
  isShippingModalOpen: boolean;
  isSignUpForAlertsModalOpen: boolean;
  isSimilarVehiclesModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isStarRatingOwnerReviewModalOpen: boolean;
  isTradeIncentiveModalOpen: boolean;
  isVehicleInquiryModalOpen: boolean;
  isWhyBuyModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  onlyConfirmCancellation: boolean;
  openToOEM: boolean;
  partnerModalContinue: () => void;
  setAboutReviewsModalOpen: ModalQueueGeneratedOpener;
  setAccidentsModalOpen: ModalQueueGeneratedOpener;
  setAccountModalOpen: ModalQueueGeneratedOpener;
  setAddCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
  setAvailabilityModalOpen: ModalQueueGeneratedOpener;
  setCatalogOemModalOpen: ModalQueueGeneratedOpener;
  setComposerUiModalOpen: ModalQueueGeneratedOpener;
  setConfirmModalOpen: (
    isOpen: boolean,
    options?: WithContinue & WithConfirmationContent
  ) => void;
  setEditCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setFeaturesAndSpecsModalOpen: (isOpen: boolean, openToOEM?: boolean) => void;
  setFinanceActivationModalOpen: ModalQueueGeneratedOpener;
  setFullScreenGalleryOpen: ModalQueueGeneratedOpener;
  setGeicoDiscountModalOpen: ModalQueueGeneratedOpener;
  setGeolocationModalOpen: ModalQueueGeneratedOpener;
  setInspectionReportModalOpen: ModalQueueGeneratedOpener;
  setLockTimerModalOpen: ModalQueueGeneratedOpener;
  setMarketValueModalOpen: ModalQueueGeneratedOpener;
  setMoreDetailsModalOpen: (isOpen: boolean, data?: MoreDetailsOptions) => void;
  setOnLoadAlertsModalOpen: ModalQueueGeneratedOpener;
  setOriginalBrochureModalOpen: ModalQueueGeneratedOpener;
  setPartnerModalOpen: (isOpen: boolean, options?: WithContinue) => void;
  setPetitionModalOpen: ModalQueueGeneratedOpener;
  setPreOrderModalOpen: ModalQueueGeneratedOpener;
  setPurchaseCompletedModalOpen: ModalQueueGeneratedOpener;
  setRecallModalOpen: ModalQueueGeneratedOpener;
  setReservableCancelModalOpen: ModalQueueGeneratedOpener;
  setReservableModalOpen: ModalQueueGeneratedOpener;
  setReturnPolicyModalOpen: ModalQueueGeneratedOpener;
  setRootDiscountModalOpen: ModalQueueGeneratedOpener;
  setSampleTermsModalOpen: ModalQueueGeneratedOpener;
  setSavingsModalOpen: ModalQueueGeneratedOpener;
  setShippingModalOpen: ModalQueueGeneratedOpener;
  setSignUpForAlertsModalOpen: (
    toggle: boolean,
    options?: SignUpForAlertsModalOptions
  ) => void;
  setSimilarVehiclesModal: ModalQueueGeneratedOpener;
  setSoftPullModalOpen: ModalQueueGeneratedOpener;
  setStarRatingOwnerReviewModalOpen: ModalQueueGeneratedOpener;
  setIsTradeIncentiveModalOpen: ModalQueueGeneratedOpener;
  setVehicleInquiryModalOpen: ModalQueueGeneratedOpener;
  setWhyBuyModalOpen: (isOpen: boolean, options?: WhyBuyModalOptions) => void;
  signUpForAlertsModalOptions: SignUpForAlertsModalOptions;
  whyBuyModalOptions: WhyBuyModalOptions;
  withModalOpen?: WithModalOpenType;
};

const defaultContext: ContextType = {
  confirmModalContinue: dummyFn,
  isAboutReviewsModalOpen: false,
  isAccidentsModalOpen: false,
  isAccountModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isAvailabilityModalOpen: false,
  isCatalogOemModalOpen: false,
  isComposerUiModalOpen: false,
  isConfirmModalOpen: false,
  isEditCoApplicantModalOpen: false,
  isFeaturesAndSpecsModalOpen: false,
  isFinanceActivationModalOpen: false,
  isFullScreenGalleryOpen: false,
  isGeicoDiscountModalOpen: false,
  isGeolocationModalOpen: false,
  isInspectionReportModalOpen: false,
  isLockTimerModalOpen: false,
  isMarketValueModalOpen: false,
  isMoreDetailsModalOpen: false,
  isOnLoadAlertsModalOpen: false,
  isOriginalBrochureModalOpen: false,
  isPartnerModalOpen: false,
  isPetitionModalOpen: false,
  isPreOrderModalOpen: false,
  isPurchaseCompletedModalOpen: false,
  isRecallModalOpen: false,
  isReservableCancelModalOpen: false,
  isReservableModalOpen: false,
  isReturnPolicyModalOpen: false,
  isRootDiscountModalOpen: false,
  isSampleTermsModalOpen: false,
  isSavingsModalOpen: false,
  isShippingModalOpen: false,
  isSignUpForAlertsModalOpen: false,
  isSimilarVehiclesModalOpen: false,
  isSoftPullModalOpen: false,
  isStarRatingOwnerReviewModalOpen: false,
  isTradeIncentiveModalOpen: false,
  isVehicleInquiryModalOpen: false,
  isWhyBuyModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  onlyConfirmCancellation: false,
  openToOEM: false,
  partnerModalContinue: dummyFn,
  setAboutReviewsModalOpen() {},
  setAccidentsModalOpen() {},
  setAccountModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setAvailabilityModalOpen() {},
  setCatalogOemModalOpen() {},
  setComposerUiModalOpen() {},
  setConfirmModalOpen() {},
  setEditCoApplicantModalOpen() {},
  setFeaturesAndSpecsModalOpen() {},
  setFinanceActivationModalOpen() {},
  setFullScreenGalleryOpen() {},
  setGeicoDiscountModalOpen() {},
  setGeolocationModalOpen() {},
  setInspectionReportModalOpen() {},
  setLockTimerModalOpen() {},
  setMarketValueModalOpen() {},
  setMoreDetailsModalOpen() {},
  setOnLoadAlertsModalOpen() {},
  setOriginalBrochureModalOpen() {},
  setPartnerModalOpen() {},
  setPetitionModalOpen() {},
  setPreOrderModalOpen() {},
  setPurchaseCompletedModalOpen() {},
  setRecallModalOpen() {},
  setReservableCancelModalOpen() {},
  setReservableModalOpen() {},
  setReturnPolicyModalOpen() {},
  setRootDiscountModalOpen() {},
  setSampleTermsModalOpen() {},
  setSavingsModalOpen() {},
  setShippingModalOpen() {},
  setSignUpForAlertsModalOpen() {},
  setSimilarVehiclesModal() {},
  setSoftPullModalOpen() {},
  setStarRatingOwnerReviewModalOpen() {},
  setIsTradeIncentiveModalOpen() {},
  setVehicleInquiryModalOpen() {},
  setWhyBuyModalOpen() {},
  signUpForAlertsModalOptions: {
    contactType:
      loadItemFromLocalStorage<ContactType>(SUBSCRIPTION_PREFERENCE) ??
      ContactType.Phone
  },
  whyBuyModalOptions: {}
};

type DummyFnType = typeof dummyFn;

const ModalContext = createContext<ContextType>(defaultContext);

export default ModalContext;

export type OwnProps = React.PropsWithChildren<{
  withModalOpen?: WithModalOpenType;
}>;

export function ModalContextProvider({ children, withModalOpen }: OwnProps) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [openToOEM, setOpenToOEM] = useState<boolean>(defaultContext.openToOEM);
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);
  const [signUpForAlertsModalOptions, setSignUpForAlertsModalOptions] =
    useState<SignUpForAlertsModalOptions>(
      defaultContext.signUpForAlertsModalOptions
    );
  const [whyBuyModalOptions, setWhyBuyModalOptions] =
    useState<WhyBuyModalOptions>(defaultContext.whyBuyModalOptions);

  const {
    setAboutReviewsModalOpen,
    setAccidentsModalOpen,
    setAccountModalOpen,
    setAddCoApplicantModalOpen,
    setAppraisalRefreshModalOpen,
    setAvailabilityModalOpen,
    setCatalogOemModalOpen,
    setComposerUiModalOpen,
    setConfirmModalOpen,
    setEditCoApplicantModalOpen,
    setFeaturesAndSpecsModalOpen,
    setFinanceActivationModalOpen,
    setFullScreenGalleryOpen,
    setGeicoDiscountModalOpen,
    setGeolocationModalOpen,
    setInspectionReportModalOpen,
    setLockTimerModalOpen,
    setMarketValueModalOpen,
    setMoreDetailsModalOpen,
    setOnLoadAlertsModalOpen,
    setOriginalBrochureModalOpen,
    setPartnerModalOpen,
    setPetitionModalOpen,
    setPreOrderModalOpen,
    setPurchaseCompletedModalOpen,
    setRecallModalOpen,
    setReservableCancelModalOpen,
    setReservableModalOpen,
    setReturnPolicyModalOpen,
    setRootDiscountModalOpen,
    setSampleTermsModalOpen,
    setSavingsModalOpen,
    setShippingModalOpen,
    setSignUpForAlertsModalOpen,
    setSimilarVehiclesModal,
    setSoftPullModalOpen,
    setStarRatingOwnerReviewModalOpen,
    setIsTradeIncentiveModalOpen,
    setVehicleInquiryModalOpen,
    setWhyBuyModalOpen
  } = useMemo(
    () => ({
      setAboutReviewsModalOpen: generateOpener(ModalId.AboutReviews),
      setAccidentsModalOpen: generateOpener(ModalId.Accidents),
      setAccountModalOpen: generateOpener(ModalId.Account),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setAvailabilityModalOpen: generateOpener(ModalId.Availability),
      setCatalogOemModalOpen: generateOpener(ModalId.CatalogOemFeatures),
      setComposerUiModalOpen: generateOpener(ModalId.ComposerUi),
      setConfirmModalOpen: generateOpener(ModalId.Confirm),
      setEditCoApplicantModalOpen: generateOpener(ModalId.EditCoApplicant),
      setFeaturesAndSpecsModalOpen: generateOpener(ModalId.FeaturesAndSpecs),
      setFinanceActivationModalOpen: generateOpener(ModalId.FinanceActivation),
      setFullScreenGalleryOpen: generateOpener(ModalId.FullScreenGallery),
      setGeicoDiscountModalOpen: generateOpener(ModalId.GeicoDiscount),
      setGeolocationModalOpen: generateOpener(ModalId.GeoLocation),
      setInspectionReportModalOpen: generateOpener(ModalId.InspectionReport),
      setLockTimerModalOpen: generateOpener(ModalId.LockTimer),
      setMarketValueModalOpen: generateOpener(ModalId.MarketValue),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setOnLoadAlertsModalOpen: generateOpener(ModalId.OnLoadAlert),
      setOriginalBrochureModalOpen: generateOpener(ModalId.OriginalBrochure),
      setPartnerModalOpen: generateOpener(ModalId.Partner),
      setPetitionModalOpen: generateOpener(ModalId.Petition),
      setPreOrderModalOpen: generateOpener(ModalId.PreOrder),
      setPurchaseCompletedModalOpen: generateOpener(ModalId.PurchaseCompleted),
      setRecallModalOpen: generateOpener(ModalId.Recall),
      setReservableCancelModalOpen: generateOpener(ModalId.ReservableCancel),
      setReservableModalOpen: generateOpener(ModalId.Reservable),
      setSavingsModalOpen: generateOpener(ModalId.Savings),
      setReturnPolicyModalOpen: generateOpener(ModalId.ReturnPolicy),
      setRootDiscountModalOpen: generateOpener(ModalId.RootDiscount),
      setSampleTermsModalOpen: generateOpener(ModalId.SampleTerms),
      setShippingModalOpen: generateOpener(ModalId.Shipping),
      setSignUpForAlertsModalOpen: generateOpener(ModalId.GetAlerts),
      setSimilarVehiclesModal: generateOpener(ModalId.SimilarVehicles),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setStarRatingOwnerReviewModalOpen: generateOpener(
        ModalId.StarRatingOwnerReview
      ),
      setIsTradeIncentiveModalOpen: generateOpener(ModalId.TradeIncentive),
      setVehicleInquiryModalOpen: generateOpener(ModalId.Activation),
      setWhyBuyModalOpen: generateOpener(ModalId.WhyBuy)
    }),
    [generateOpener]
  );

  // Features and Specs
  const setFeaturesAndSpecsOpenWithOptions = useCallback<
    ContextType['setFeaturesAndSpecsModalOpen']
  >(
    (isOpen: boolean, openToOEMPanel: boolean = defaultContext.openToOEM) => {
      setOpenToOEM(openToOEMPanel);
      setFeaturesAndSpecsModalOpen(isOpen);
    },
    [setFeaturesAndSpecsModalOpen, setOpenToOEM]
  );

  // Cancel purchase, diesel, manual transmission confirmation
  const [confirmModalContinue, setConfirmModalContinue] = useState<DummyFnType>(
    defaultContext.confirmModalContinue
  );
  const [onlyConfirmCancellation, setOnlyConfirmCancellation] = useState(
    defaultContext.onlyConfirmCancellation
  );

  const setConfirmModalOpenWithOptions = useCallback(
    (isOpen: boolean, options: WithContinue & WithConfirmationContent = {}) => {
      setConfirmModalOpen(isOpen);
      setConfirmModalContinue(() => options.onContinue ?? dummyFn);
      setOnlyConfirmCancellation(options.onlyConfirmCancellation ?? false);
    },
    [setConfirmModalOpen]
  );

  // Partner confirmation
  const [partnerModalContinue, setPartnerModalContinue] =
    useState<DummyFnType>(dummyFn);

  const setPartnerOpenWithOptions = useCallback(
    (isOpen: boolean, { onContinue = dummyFn }: WithContinue = {}) => {
      setPartnerModalOpen(isOpen);
      setPartnerModalContinue(() => onContinue);
    },
    [setPartnerModalOpen]
  );

  //More Details Modal
  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (
      isOpen: boolean,
      options: MoreDetailsOptions = defaultContext.moreDetailsOptions
    ) => {
      setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const setSignUpForAlertsModalOpenWithOptions = useCallback<
    ContextType['setSignUpForAlertsModalOpen']
  >(
    (isOpen: boolean, options?: SignUpForAlertsModalOptions) => {
      if (options) setSignUpForAlertsModalOptions(options);
      setSignUpForAlertsModalOpen(isOpen);
    },
    [setSignUpForAlertsModalOpen]
  );

  const setWhyBuyModalOpenWithOptions = useCallback<
    ContextType['setWhyBuyModalOpen']
  >(
    (
      isOpen: boolean,
      options: WhyBuyModalOptions = defaultContext.whyBuyModalOptions
    ) => {
      setWhyBuyModalOptions(options);
      setWhyBuyModalOpen(isOpen);
    },
    [setWhyBuyModalOpen]
  );

  useEffect(() => {
    if (!withModalOpen) return;

    if (withModalOpen === WithModalOpenType.Geolocation) {
      setGeolocationModalOpen(true);
    }

    if (withModalOpen === WithModalOpenType.SoftPull) {
      setSoftPullModalOpen(true);
    }

    removeQueryParam([CustomConfigs.withModalOpen]);
  }, [setGeolocationModalOpen, setSoftPullModalOpen, withModalOpen]);

  const value = useMemo<ContextType>(
    (): ContextType => ({
      confirmModalContinue,
      isAboutReviewsModalOpen: currentModalId === ModalId.AboutReviews,
      isAccidentsModalOpen: currentModalId === ModalId.Accidents,
      isAccountModalOpen: currentModalId === ModalId.Account,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isAvailabilityModalOpen: currentModalId === ModalId.Availability,
      isCatalogOemModalOpen: currentModalId === ModalId.CatalogOemFeatures,
      isComposerUiModalOpen: currentModalId === ModalId.ComposerUi,
      isConfirmModalOpen: currentModalId === ModalId.Confirm,
      isEditCoApplicantModalOpen: currentModalId === ModalId.EditCoApplicant,
      isFeaturesAndSpecsModalOpen: currentModalId === ModalId.FeaturesAndSpecs,
      isFinanceActivationModalOpen:
        currentModalId === ModalId.FinanceActivation,
      isFullScreenGalleryOpen: currentModalId === ModalId.FullScreenGallery,
      isGeicoDiscountModalOpen: currentModalId === ModalId.GeicoDiscount,
      isGeolocationModalOpen: currentModalId === ModalId.GeoLocation,
      isInspectionReportModalOpen: currentModalId === ModalId.InspectionReport,
      isLockTimerModalOpen: currentModalId === ModalId.LockTimer,
      isMarketValueModalOpen: currentModalId === ModalId.MarketValue,
      isMoreDetailsModalOpen: currentModalId === ModalId.MoreDetails,
      isOnLoadAlertsModalOpen: currentModalId === ModalId.OnLoadAlert,
      isOriginalBrochureModalOpen: currentModalId === ModalId.OriginalBrochure,
      isPartnerModalOpen: currentModalId === ModalId.Partner,
      isPetitionModalOpen: currentModalId === ModalId.Petition,
      isPreOrderModalOpen: currentModalId === ModalId.PreOrder,
      isPurchaseCompletedModalOpen:
        currentModalId === ModalId.PurchaseCompleted,
      isRecallModalOpen: currentModalId === ModalId.Recall,
      isReservableCancelModalOpen: currentModalId === ModalId.ReservableCancel,
      isReservableModalOpen: currentModalId === ModalId.Reservable,
      isReturnPolicyModalOpen: currentModalId === ModalId.ReturnPolicy,
      isRootDiscountModalOpen: currentModalId === ModalId.RootDiscount,
      isSampleTermsModalOpen: currentModalId === ModalId.SampleTerms,
      isSavingsModalOpen: currentModalId === ModalId.Savings,
      isShippingModalOpen: currentModalId === ModalId.Shipping,
      isSignUpForAlertsModalOpen: currentModalId === ModalId.GetAlerts,
      isSimilarVehiclesModalOpen: currentModalId === ModalId.SimilarVehicles,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isStarRatingOwnerReviewModalOpen:
        currentModalId === ModalId.StarRatingOwnerReview,
      isTradeIncentiveModalOpen: currentModalId === ModalId.TradeIncentive,
      isVehicleInquiryModalOpen: currentModalId === ModalId.Activation,
      isWhyBuyModalOpen: currentModalId === ModalId.WhyBuy,
      moreDetailsOptions,
      onlyConfirmCancellation,
      openToOEM,
      partnerModalContinue,
      setAboutReviewsModalOpen,
      setAccidentsModalOpen,
      setAccountModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setAvailabilityModalOpen,
      setCatalogOemModalOpen,
      setComposerUiModalOpen,
      setConfirmModalOpen: setConfirmModalOpenWithOptions,
      setEditCoApplicantModalOpen,
      setFeaturesAndSpecsModalOpen: setFeaturesAndSpecsOpenWithOptions,
      setFinanceActivationModalOpen,
      setFullScreenGalleryOpen,
      setGeicoDiscountModalOpen,
      setGeolocationModalOpen,
      setInspectionReportModalOpen,
      setLockTimerModalOpen,
      setMarketValueModalOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setOnLoadAlertsModalOpen,
      setOriginalBrochureModalOpen,
      setPartnerModalOpen: setPartnerOpenWithOptions,
      setPetitionModalOpen,
      setPreOrderModalOpen,
      setPurchaseCompletedModalOpen,
      setRecallModalOpen,
      setReservableCancelModalOpen,
      setReservableModalOpen,
      setReturnPolicyModalOpen,
      setRootDiscountModalOpen,
      setSampleTermsModalOpen,
      setSavingsModalOpen,
      setShippingModalOpen,
      setSignUpForAlertsModalOpen: setSignUpForAlertsModalOpenWithOptions,
      setSimilarVehiclesModal,
      setSoftPullModalOpen,
      setStarRatingOwnerReviewModalOpen,
      setIsTradeIncentiveModalOpen,
      setVehicleInquiryModalOpen,
      setWhyBuyModalOpen: setWhyBuyModalOpenWithOptions,
      signUpForAlertsModalOptions,
      whyBuyModalOptions,
      withModalOpen
    }),
    [
      confirmModalContinue,
      currentModalId,
      moreDetailsOptions,
      onlyConfirmCancellation,
      openToOEM,
      partnerModalContinue,
      setAboutReviewsModalOpen,
      setAccidentsModalOpen,
      setAccountModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setAvailabilityModalOpen,
      setCatalogOemModalOpen,
      setComposerUiModalOpen,
      setConfirmModalOpenWithOptions,
      setEditCoApplicantModalOpen,
      setFeaturesAndSpecsOpenWithOptions,
      setFinanceActivationModalOpen,
      setFullScreenGalleryOpen,
      setGeicoDiscountModalOpen,
      setGeolocationModalOpen,
      setInspectionReportModalOpen,
      setIsTradeIncentiveModalOpen,
      setLockTimerModalOpen,
      setMarketValueModalOpen,
      setMoreDetailsModalOpenWithVehicle,
      setOnLoadAlertsModalOpen,
      setOriginalBrochureModalOpen,
      setPartnerOpenWithOptions,
      setPetitionModalOpen,
      setPreOrderModalOpen,
      setPurchaseCompletedModalOpen,
      setRecallModalOpen,
      setReservableCancelModalOpen,
      setReservableModalOpen,
      setReturnPolicyModalOpen,
      setRootDiscountModalOpen,
      setSampleTermsModalOpen,
      setSavingsModalOpen,
      setShippingModalOpen,
      setSignUpForAlertsModalOpenWithOptions,
      setSimilarVehiclesModal,
      setSoftPullModalOpen,
      setStarRatingOwnerReviewModalOpen,
      setVehicleInquiryModalOpen,
      setWhyBuyModalOpenWithOptions,
      signUpForAlertsModalOptions,
      whyBuyModalOptions,
      withModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedModalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ModalContext.Provider value={generateModalContext(mockValue)}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  return React.useContext(ModalContext);
}
